/* eslint-disable newline-before-return */
import { useEffect, useMemo, useState } from "react";

import { normalizeJson } from "@/components/FormEngine/formatters/normalizer";
import { buildFlatFieldMap } from "@/components/FormEngine/helpers/buildFlatMapFields";
import { enrichImpacts } from "@/components/FormEngine/helpers/enrichImpacts";
import {
  aggregateOverridesBySource,
  applyOverridesTransition,
  buildSourceOverridesSlot,
  mergeBaseWithOverrides,
} from "@/components/FormEngine/helpers/overrides";
import { parseRawForm } from "@/components/FormEngine/parseRawForm";
import {
  FieldImpact,
  FormFieldValue,
  ParsedForm,
  RawField,
} from "@/components/FormEngine/types";

const EMPTY_PARSED: ParsedForm = { invalidFields: [], steps: new Map() };

export const useFormEngine = () => {
  const [initialForm, setInitialForm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parsedForm, setParsedForm] = useState<ParsedForm | null>(EMPTY_PARSED);

  // Map RAW immuable (définition initiale)
  const [baseMap, setBaseMap] = useState<Record<string, RawField>>({});
  // Overrides par source
  const [overridesBySource, setOverridesBySource] = useState<
    Record<string, Record<string, Partial<RawField>>>
  >({});

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await fetch("/fixtures/data-2.json");
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
        const data = await response.json();
        setInitialForm(JSON.stringify(data, null, 2));
      } catch (e) {
        console.error("Error loading initial JSON:", e);
        setError("Unable to load initial JSON file.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const handleLoadFromFile = (file: File | null) => {
    if (!file) return;
    if (file.type !== "application/json") {
      setError("File must be in JSON format.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      try {
        const jsonData = JSON.parse(text);
        setInitialForm(JSON.stringify(jsonData, null, 2));
        setParsedForm(null);
        setError(null);
      } catch {
        setError("File content is not valid JSON.");
      }
    };
    reader.onerror = () => setError("Unable to read file.");
    reader.readAsText(file);
  };

  const handleApplyJson = () => {
    try {
      const root = JSON.parse(initialForm);

      setBaseMap(buildFlatFieldMap(root)); // base immuable

      const parsed = parseRawForm(root); // parse
      const enriched = enrichImpacts(parsed); // enrich impacts (name -> id/path)

      setOverridesBySource({}); // reset overrides
      setParsedForm(enriched);
      setError(null);
    } catch {
      setError("JSON syntax error.");
      setParsedForm(null);
    }
  };

  // Agrégat des overrides (OR sur hidden)
  const overrides = useMemo(
    () => aggregateOverridesBySource(overridesBySource),
    [overridesBySource],
  );

  // Map effectif (base ⊕ overrides)
  const fieldsMap = useMemo(
    () => mergeBaseWithOverrides(baseMap, overrides),
    [baseMap, overrides],
  );

  // JSON normalisé basé sur le map effectif
  const normalized = useMemo(
    () => normalizeJson(parsedForm ?? EMPTY_PARSED, fieldsMap),
    [parsedForm, fieldsMap],
  );

  const transformedJson = useMemo(
    () => JSON.stringify(normalized, null, 2),
    [normalized],
  );

  const transformedOutput = useMemo(
    () => JSON.stringify({ productCreationForm: {} }, null, 2),
    [],
  );

  const anomaliesCount = useMemo(
    () =>
      Array.isArray((normalized as any)?.anomalies)
        ? normalized.anomalies.length
        : 0,
    [normalized],
  );

  /**
   * Change contrôlé :
   * - Construit le slot overrides de la source
   * - Purge les cibles impactées chez les autres sources
   * - Bump la source (évaluation en dernier)
   */
  const applyPathBasedChange = (
    fieldPathSegments: string[],
    next: FormFieldValue,
    impacts?: FieldImpact[],
  ) => {
    const sourceKey = fieldPathSegments.join(".");
    const { slot, impactedTargets } = buildSourceOverridesSlot(
      sourceKey,
      next,
      impacts,
    );

    setOverridesBySource((prev) =>
      applyOverridesTransition(prev, sourceKey, slot, impactedTargets),
    );
  };

  return {
    initialForm,
    setInitialForm,
    isLoading,
    error,
    parsedForm,
    handleLoadFromFile,
    handleApplyJson,
    normalized,
    transformedJson,
    transformedOutput,
    anomaliesCount,
    fieldsMap,
    applyPathBasedChange,
  };
};
